import { useEffect, useState, ReactNode } from 'react';

import classNames from 'classnames';
import { SimpleTreeView as MUITreeView } from '@mui/x-tree-view/SimpleTreeView';

import { Icon } from '../Icon';

import { TreeItem } from './TreeItem';
import { TreeViewItem as Item } from './Item.interface';
import { useStyles } from './TreeView.styles';

/**
 * Свойства компонента {@link TreeView}
 */
interface TreeViewProps {
  /**
   * Функция обратного вызова, вызываемая при выборе элемента дерева.
   * Принимает три аргумента: идентификатор узла, метку узла и сам элемент.
   */
  onSelect: (value: string, label?: string, item?: Item) => void;

  /**
   * Асинхронная функция для загрузки дочерних элементов дерева.
   * Принимает один аргумент: идентификатор узла и возвращает промис, который разрешается в массив элементов.
   */
  onChildrenLoad?: (unitId: string) => Promise<Item[]>;

  /**
   * Массив объектов данных, представляющих элементы дерева.
   */
  items: Item[];

  /**
   * Определяет, следует ли выбирать родительский узел при сворачивании дочернего узла.
   *
   * @defaultValue `true`
   */
  selectOnShrink?: boolean;

  /**
   * Определяет, следует ли отображать линии для элементов дерева.
   *
   * @defaultValue `true`
   */
  showRulers?: boolean;

  /**
   * Определяет, следует ли отображать счетчики, связанные с элементами дерева.
   *
   * @defaultValue `true`
   */
  showCounters?: boolean;

  /**
   * Добавляет padding-bottom 56px.
   *
   * @defaultValue `false`
   */
  withPaddingBottom?: boolean;

  /**
   * Идентификатор текущего выбранного узла для одиночного выбора.
   */
  selected: string;

  /**
   * Функция для кастомного рендеринга опции.
   */
  renderOption?: (item: Item) => ReactNode;
}

const parseParents: (
  items: Item[],
  parents?: string[],
) => Record<string, string[]> = (items, parents = []) => {
  let result: Record<string, string[]> = {};

  items.forEach((item) => {
    result[item.id] = parents;

    if (item.children && item.children.length) {
      result = {
        ...result,
        ...parseParents(item.children, [...parents, item.id]),
      };
    }
  });

  return result;
};

/**
 * Компонент древовидного представления, который поддерживает как одиночный, так и множественный выбор.
 * Он может разворачивать и сворачивать элементы дерева, а также обрабатывать асинхронную загрузку дочерних элементов.
 * Компонент также предоставляет визуальные индикаторы для состояний развернутости и выбора.
 *
 * @public
 * @param onSelect - Функция обратного вызова, вызываемая при выборе элемента дерева.
 * @param onChildrenLoad - Асинхронная функция для загрузки дочерних элементов дерева.
 * @param items - Массив объектов данных, представляющих элементы дерева.
 * @param selectOnShrink - Определяет, следует ли выбирать родительский узел при сворачивании дочернего узла.
 * @param showRulers - Определяет, следует ли отображать линии для элементов дерева.
 * @param showCounters - Определяет, следует ли отображать счетчики, связанные с элементами дерева.
 * @param withPaddingBottom - Добавляет padding-bottom 56px.
 * @param selected - Идентификатор текущего выбранного узла для одиночного выбора.
 * @param renderOption - Функция для кастомного рендеринга опции.
 * @returns Компонент TreeView.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link TreeViewProps}
 */
export const TreeView = (props: TreeViewProps) => {
  const {
    onSelect,
    onChildrenLoad,
    items,
    selectOnShrink = true,
    showRulers = true,
    showCounters = true,
    withPaddingBottom = false,
    renderOption,
  } = props;

  const { classes } = useStyles({ withPaddingBottom });

  const [expanded, expand] = useState<string[]>([]);
  const parentsOfSelected =
    (props.selected && parseParents(items)[props.selected]) || [];

  useEffect(() => {
    expand((previous) => [
      ...previous,
      ...(parseParents(items)[props.selected] || []),
    ]);
  }, [props.selected, items]);

  const customBulletIcon = () => (
    <Icon name="circle_small_filled" size="20" color="text.light.tertirary" />
  );

  const customExpandMoreIcon = () => (
    <Icon name="dropdown_outline" size="16" color="text.light.tertirary" />
  );

  const customExpandLessIcon = () => (
    <Icon
      name="dropdown_flipped_outline"
      size="16"
      color="text.light.tertirary"
    />
  );

  return (
    <MUITreeView
      expandedItems={expanded}
      className={classNames(classes.root, 'aqa_tree_view')}
      slots={{
        collapseIcon: customExpandLessIcon,
        endIcon: customBulletIcon,
        expandIcon: customExpandMoreIcon,
      }}
      data-testid="tree-view-root-test-id"
      selectedItems={props.selected}
    >
      {items.map((item) => (
        <TreeItem
          key={item.id}
          item={item}
          showRulers={showRulers}
          showCounters={showCounters}
          onSelect={onSelect}
          expanded={expanded}
          onExpand={(value) =>
            expand((previous) => {
              if (previous.includes(value)) {
                if (selectOnShrink && parentsOfSelected.includes(value)) {
                  onSelect(value);
                }
                return previous.filter(
                  (previousExpanded) => previousExpanded !== value,
                );
              }

              return [...previous, value];
            })
          }
          onChildrenLoad={onChildrenLoad}
          selected={props.selected}
          renderOption={renderOption}
        />
      ))}
    </MUITreeView>
  );
};
